import { FUNNEL_QUESTIONNAIRE_ELIGIBILITY } from "src/constants/misc";
import type {
  CustomQuestionnaireAnswer,
  CustomQuestionnaireChoice,
  CustomQuestionnaireFilterAnswerOptions
} from "src/state/QuestionnaireCubit/QuestionnaireState";
import { factoryCampaignInstances } from "./SignupCustomCampaignGenericInstances";
import type FactorySignupCustomCampaign from "./FactorySignupCustomCampaign";
import { EligibilityQuestionId } from "./EligibilityQuestionnaireFields";

function choiceAnswerMatches(
  override: CustomQuestionnaireAnswer,
  userAnswer: CustomQuestionnaireAnswer
): boolean {
  const overrideChoice = override.fieldValue as CustomQuestionnaireChoice;
  const userChoice = userAnswer.fieldValue as CustomQuestionnaireChoice;
  return userChoice?.choiceId === overrideChoice?.choiceId;
}

function allOverridesMatch(
  campaignOverrides: CustomQuestionnaireAnswer[],
  userAnswers: CustomQuestionnaireAnswer[]
): boolean {
  return campaignOverrides.every((override) => {
    const userAnswer = userAnswers.find(
      (a) => a.questionId === override.questionId
    );
    if (!userAnswer) {
      return false;
    }

    if (override.fieldType === "choice") {
      return choiceAnswerMatches(override, userAnswer);
    }

    return false;
  });
}

// Campaigns with many valid sub-employers (e.g. SmithRx, Prescryptive) can't
// pin a single answerOverrides value — they instead restrict the employer
// question to an include-list via filterAnswerOptions. That list is also a
// valid signal that the user's answers belong to this campaign, so it must
// be checked here too, or such a campaign can never be matched outside its
// own direct funnel route (e.g. a user reached via the generic "universal"
// funnel who picks one of SmithRx's sub-employers).
function allFiltersMatch(
  campaignFilters: CustomQuestionnaireFilterAnswerOptions[],
  userAnswers: CustomQuestionnaireAnswer[]
): boolean {
  return campaignFilters.every((filter) => {
    const userAnswer = userAnswers.find(
      (a) => a.questionId === filter.questionId
    );
    const choiceId = (userAnswer?.fieldValue as CustomQuestionnaireChoice)
      ?.choiceId;
    if (!choiceId) {
      return false;
    }
    if (filter.excludeOptions?.includes(choiceId)) {
      return false;
    }
    if (filter.includeOptions && !filter.includeOptions.includes(choiceId)) {
      return false;
    }
    return true;
  });
}

// A partner's own campaign (e.g. Transcarent) can force a generic answer
// (e.g. healthPlan=Transcarent) into every submission on its route — see
// QuestionnaireCubit.generateOutputData, which unconditionally injects a
// campaign's answerOverrides regardless of what the form actually captured.
// That generic, partner-level signal must never shadow a more specific
// employer-based sub-campaign (e.g. Bank of New York) that also matches, so
// employer-question matches are checked before any other kind.
function hasEmployerSignal(campaign: FactorySignupCustomCampaign): boolean {
  const overrides =
    campaign.answerOverrides?.[FUNNEL_QUESTIONNAIRE_ELIGIBILITY] ?? [];
  const filters =
    campaign.filterAnswerOptions?.[FUNNEL_QUESTIONNAIRE_ELIGIBILITY] ?? [];

  return (
    overrides.some((o) => o.questionId === EligibilityQuestionId.employer) ||
    filters.some((f) => f.questionId === EligibilityQuestionId.employer)
  );
}

/**
 * Find a campaign whose eligibility answerOverrides (or filterAnswerOptions
 * include-list) match the user's answers. Returns the matching campaign
 * instance, or null if no match found.
 */
export function findMatchingCampaignByAnswers(
  userAnswers: CustomQuestionnaireAnswer[]
): FactorySignupCustomCampaign | null {
  if (!userAnswers.length) {
    return null;
  }

  const campaigns = factoryCampaignInstances();
  const orderedCampaigns = [
    ...campaigns.filter(hasEmployerSignal),
    ...campaigns.filter((campaign) => !hasEmployerSignal(campaign))
  ];

  for (const campaign of orderedCampaigns) {
    const overrides =
      campaign.answerOverrides?.[FUNNEL_QUESTIONNAIRE_ELIGIBILITY];

    if (overrides?.length && allOverridesMatch(overrides, userAnswers)) {
      return campaign;
    }

    const filters =
      campaign.filterAnswerOptions?.[FUNNEL_QUESTIONNAIRE_ELIGIBILITY];

    if (filters?.length && allFiltersMatch(filters, userAnswers)) {
      return campaign;
    }
  }

  return null;
}
